/**
 * Lifecycle-Status (vom Backend als String geliefert).
 * Mapping zu i18n-Keys für text() – keine Magic-Strings in der UI.
 */

/** Vom Backend gelieferte Werte (Person.LifecycleStatus / PersonKind.ToLifecycleString). */
export const LIFECYCLE_MEMBER = "Member";
export const LIFECYCLE_LEAD = "Lead";
export const LIFECYCLE_FORMER = "Former";
export const STUDIO_APP_AUDIENCE_STAFF = "Staff";
const GENDER_FEMALE = 2;

/** i18n-Keys für die Anzeige (mit text() übersetzen). */
export const LIFECYCLE_LABEL_KEYS: Record<string, string> = {
  [LIFECYCLE_MEMBER]: "Member",
  [LIFECYCLE_LEAD]: "Prospect",
  [LIFECYCLE_FORMER]: "Former",
};

export type LifecycleTagColor = "blue" | "cyan" | "default";

/** Farbe pro Status für das Tag. */
export const LIFECYCLE_TAG_COLORS: Record<string, LifecycleTagColor> = {
  [LIFECYCLE_MEMBER]: "blue",
  [LIFECYCLE_LEAD]: "cyan",
  [LIFECYCLE_FORMER]: "default",
};

export function normalizeLifecycleStatus(status: string | null | undefined): string {
  return (status ?? "").toString().trim();
}

export function lifecycleLabelKey(status: string | null | undefined): string | null {
  const s = normalizeLifecycleStatus(status);
  return s && LIFECYCLE_LABEL_KEYS[s] ? LIFECYCLE_LABEL_KEYS[s] : null;
}

export function lifecycleLabel(status: string | null | undefined, gender: number | null | undefined, text: (key: string) => string): string {
  const s = normalizeLifecycleStatus(status);
  if (s === LIFECYCLE_LEAD && Number(gender) === GENDER_FEMALE) return text("Prospect female");
  const key = lifecycleLabelKey(s);
  return key ? text(key) : s;
}

export function lifecycleTagColor(status: string | null | undefined): LifecycleTagColor {
  const s = normalizeLifecycleStatus(status);
  return (s && LIFECYCLE_TAG_COLORS[s]) || "default";
}

/** Optionen für Select/Dropdown (value = API-Wert, label = übersetzt). Für Zuweisungen immer mit i18n nutzen. */
export function getLifecycleStatusSelectOptions(text: (key: string) => string): { value: string; label: string }[] {
  return [
    { value: LIFECYCLE_MEMBER, label: text(lifecycleLabelKey(LIFECYCLE_MEMBER) ?? "Member") },
    { value: LIFECYCLE_LEAD, label: text(lifecycleLabelKey(LIFECYCLE_LEAD) ?? "Prospect") },
    { value: LIFECYCLE_FORMER, label: text(lifecycleLabelKey(LIFECYCLE_FORMER) ?? "Former") }
  ];
}

/**
 * Anzeige-Schlüssel einer Studio-App-Zielgruppe. Deckt zusätzlich „Mitarbeiter“ ab, das kein
 * Lebenszyklus ist — ohne diese Ergänzung wurde Personal in Listen fälschlich als „Mitglied“ gezeigt.
 */
export function studioAppAudienceLabelKey(audience: string | null | undefined): string | null {
  const s = normalizeLifecycleStatus(audience);
  if (s === STUDIO_APP_AUDIENCE_STAFF) return "Staff";
  if (s === LIFECYCLE_FORMER) return "Studio app audience former";
  return lifecycleLabelKey(s);
}

/** Zielgruppen der Studio-App: bestehende Lebenszyklen plus Mitarbeiter. */
export function getStudioAppAudienceSelectOptions(text: (key: string) => string): { value: string; label: string }[] {
  return [
    ...getLifecycleStatusSelectOptions(text).map((option) =>
      option.value === LIFECYCLE_FORMER
        ? { ...option, label: text("Studio app audience former") }
        : option
    ),
    { value: STUDIO_APP_AUDIENCE_STAFF, label: text("Staff") }
  ];
}
